import { ViewportScroller } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AuthService, RoutingService } from '@spartacus/core';
import { LAUNCH_CALLER, LaunchDialogService } from '@spartacus/storefront';
import { combineLatest, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { ModalCloseReason } from '../../../../core/modal';
import {
  AccountingCodeList,
  Cart,
  CartError,
  CartNotification,
  CartType,
  OrderEntry,
  OrderStatus,
  Price,
  TinyCart,
} from '../../../../core/model';
import { OrderApprovalFacade } from '../../../../core/user';
import { WindowRef } from '../../../../core/window';
import { CART_TYPE_PARAM_KEY, DrawerService, getTextToDisplayInsteadOfPrice, WindowUtils } from '../../../../shared';
import {
  RemoveEntryEvent,
  ReplaceEntryEvent,
  UpdateEntryNoteEvent,
  UpdateEntryQuantityEvent,
  UpdateEntryStatisticsCodeEvent,
} from '../../../../shared/components/cart-entries-table/cart-entries-table.component';
import {
  CreateShoppingListModalData,
  CreateShoppingListModalType,
} from '../../../../shared/components/create-shopping-list-modal/create-shopping-list-modal.component';
import { DrawerTemplate, DrawerTemplateRef } from '../../../../shared/components/drawer/drawer-template.component';
import { ORDER_ENTRY_CONFIG, OrderEntryConfig } from '../../../../shared/components/order-entry';
import { CheckoutFacade } from '../../../checkout/base';
import { SoldToFacade } from '../../../sold-to-base';
import { ActiveCartFacade, CartAccountingCodesFacade, CartValidatorFacade, TinyCartFacade } from '../../base';

@Component({
  selector: 'py-cart-drawer',
  templateUrl: './cart-drawer.component.html',
  styleUrls: ['./cart-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: ORDER_ENTRY_CONFIG,
      useValue: <OrderEntryConfig>{
        deletable: true,
        enableNoteControl: true,
        enableFullPalletNoteControl: false,
        enableStatisticsCodeControl: true,
        enableMandatoryNoteControlOption: false,
        enableMandatoryStatisticsCodeControl: false,
        enableAccountingCodes: true,
        enableQuantityControl: true,
        enableStockInfo: true,
        enableNameMarking: true,
        showNameMarkingButton: true,
        enableCutting: true,
        showCuttingButton: true,
        showCuttingPerpendicular: true,
        showCuttingDustFree: true,
        enableReelCutting: true,
        showReelCuttingButton: true,
        enableReaming: true,
        showReamingButton: true,
        translationKeyPrefix: 'cart',
        selectable: false,
        outOfStockAction: 'cart.replaceItem_action',
        showPalletFlags: true,
        enableFullPalletUpgrade: false,
        showWarehouseName: false,
        showOrderReturnEntryBadge: false,
        loadAndShowArticlePriceInsteadOfEntryPrice: true,
        enablePrePrintedLabels: true,
        showPrePrintedLabelsButton: true,
      },
    },
  ],
  standalone: false,
})
export class CartDrawerComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly stockCartType: CartType = CartType.stock;

  @ViewChild('headerSection') headerSection: ElementRef<HTMLDivElement>;
  @ViewChild('bodySection') bodySection: ElementRef<HTMLDivElement>;
  @ViewChildren('drawerCartEntriesTableChild') drawerCartEntriesTableChild: QueryList<any>;

  private updateCartDrawerBodyPosition$ = new Subject<void>();
  private isViewCart = true;

  tinyCarts$: Observable<TinyCart[]>;
  hasMultipleTinyCarts$: Observable<boolean>;
  selectedTinyCartCode$ = new ReplaySubject<string>(1);
  entries$: Observable<OrderEntry[]>;
  numberOfEntries$: Observable<number>;
  notifications$: Observable<CartNotification[]>;
  showEmptyNotification$: Observable<boolean>;
  loading$: Observable<boolean>;
  isUserLoggedIn$: Observable<boolean>;
  totalPrice$: Observable<Price>;
  hasCartEditStatus$: Observable<boolean>;
  approvalOrderRef$: Observable<string>;
  isCheckoutEnabled$: Observable<boolean>;
  isCreditBlocked$: Observable<boolean>;
  isMaxOrderLinesExceeded$: Observable<boolean>;
  drawerChildren: DrawerTemplate[];
  activeTemplateKey?: DrawerTemplateRef;
  cartType$: Observable<CartType>;
  cartErrors$: Observable<CartError[] | null>;
  accountingCodeList$: Observable<AccountingCodeList>;
  accountingCodeListLoading$: Observable<boolean>;
  accountingCodesMandatoryButNoneAvailable$: Observable<boolean>;
  cartTypeQueryParams: Record<string, CartType>;
  cartType: CartType;
  hasDiscontinuedArticles$: Observable<boolean>;
  prePrintedLabelEntryMixedWithNonPrePrintedEntry$: Observable<boolean>;

  private subscriptions = new Subscription();

  formGroup = new UntypedFormGroup({
    formGroupEntries: this.fb.array([]),
  });

  get formGroupEntries(): UntypedFormArray {
    return this.formGroup.get('formGroupEntries') as UntypedFormArray;
  }

  get textInsteadOfPrice(): string | undefined {
    return getTextToDisplayInsteadOfPrice(this.cartType);
  }

  private get headerHeight(): number {
    const parentElement: HTMLDivElement = <HTMLDivElement>this.headerSection.nativeElement.parentNode;
    return parentElement.offsetHeight;
  }

  constructor(
    private authService: AuthService,
    private tinyCartService: TinyCartFacade,
    private activeCartService: ActiveCartFacade,
    private checkoutService: CheckoutFacade,
    private soldToService: SoldToFacade,
    private cartAccountingCodesService: CartAccountingCodesFacade,
    private cartValidatorService: CartValidatorFacade,
    private orderApprovalService: OrderApprovalFacade,
    private fb: UntypedFormBuilder,
    private routingService: RoutingService,
    private elementRef: ElementRef,
    private windowRef: WindowRef,
    private windowUtils: WindowUtils,
    private renderer: Renderer2,
    private cd: ChangeDetectorRef,
    private drawerService: DrawerService,
    private viewportScroller: ViewportScroller,
    private launchDialogService: LaunchDialogService
  ) {}

  ngOnInit(): void {
    this.initObservables();

    this.subscriptions.add(
      this.tinyCarts$
        .pipe(
          filter((tinyCarts) => tinyCarts.length > 0),
          map((tinyCarts) => tinyCarts[0]),
          map((tinyCart) => tinyCart.code)
        )
        .subscribe((code) => {
          this.selectedTinyCartCode$.next(code);
        })
    );

    this.subscriptions.add(
      this.cartType$
        .pipe(
          tap((cartType) => {
            this.cartTypeQueryParams = { [CART_TYPE_PARAM_KEY]: cartType };
            this.cartType = cartType;
          })
        )
        .subscribe()
    );

    this.updateCartDrawerBodyPosition$.next();

    this.updateCartDrawerBodyPosition$
      .pipe(
        map(() => this.headerHeight),
        distinctUntilChanged((prevHeight, currHeight) => prevHeight === currHeight)
      )
      .subscribe((height) => this.renderer.setStyle(this.bodySection.nativeElement, 'margin-top', `${height}px`));
  }

  ngAfterViewInit(): void {
    this.updateCartDrawerBodyPosition$.next();
    this.listenToConditionalMessagesOrWindowResizeChange();
  }

  private listenToConditionalMessagesOrWindowResizeChange(): void {
    // Update cart drawer body position when one of conditional messages will be rendered or removed or if window will be resized
    combineLatest([
      this.isMaxOrderLinesExceeded$,
      this.hasDiscontinuedArticles$,
      this.prePrintedLabelEntryMixedWithNonPrePrintedEntry$,
      this.notifications$,
      this.accountingCodesMandatoryButNoneAvailable$,
      this.hasMultipleTinyCarts$,
      this.isCreditBlocked$,
      this.windowRef.resize$,
    ]).subscribe(() => {
      this.cd.detectChanges();
      this.updateCartDrawerBodyPosition$.next();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onCartEntryRemove(event: RemoveEntryEvent) {
    const { entry } = event;

    this.cartType$.pipe(take(1)).subscribe((cartType) => {
      this.activeCartService.removeEntry(entry, cartType);
    });
  }

  onCartEntryReplace(event: ReplaceEntryEvent) {
    const { entry, replacementArticle } = event;

    this.cartType$.pipe(take(1)).subscribe((cartType) => {
      this.activeCartService.replaceEntry(entry, replacementArticle, cartType);
    });
  }

  onCartEntryUpdateQuantity(event: UpdateEntryQuantityEvent) {
    const { entry } = event;

    this.cartType$.pipe(take(1)).subscribe((cartType) => {
      this.activeCartService.updateEntry(entry, event.quantity, event.unit, cartType);
    });
  }

  onCartEntryUpdateNote(event: UpdateEntryNoteEvent) {
    const { entry } = event;

    this.cartType$.pipe(take(1)).subscribe((cartType) => {
      this.activeCartService.updateEntryNote(entry, event.note, cartType);
    });
  }

  onCartEntryUpdateStatisticsCode(event: UpdateEntryStatisticsCodeEvent) {
    const { entry } = event;

    this.cartType$.pipe(take(1)).subscribe((cartType) => {
      this.activeCartService.updateEntryStatisticsCode(entry, event.code, cartType);
    });
  }

  onCreateShoppingList(): void {
    const modalData: CreateShoppingListModalData = {
      modalType: CreateShoppingListModalType.CREATE_FROM_CART,
      cartType: this.cartType,
    };

    this.launchDialogService.openDialogAndSubscribe(LAUNCH_CALLER.CREATE_SHOPPING_LIST, undefined, modalData);
  }

  onEmptyCart(): void {
    this.cartType$.pipe(take(1)).subscribe((cartType) => {
      this.launchDialogService.openDialogAndSubscribe(LAUNCH_CALLER.EMPTY_CART, undefined, cartType);
    });
  }

  onTinyCartSelect(code: string) {
    this.selectedTinyCartCode$.next(code);
  }

  onAbortCartEdit(): void {
    this.openAbortCartEditModal();
  }

  private initObservables(): void {
    const tinyCarts$ = this.tinyCartService.getTinyCartsLoaded().pipe(
      filter((loaded) => !!loaded),
      switchMap((_) => this.tinyCartService.getTinyCarts()),
      map((tinyCarts) => tinyCarts || []),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    const selectedTinyCart$ = combineLatest([this.selectedTinyCartCode$, tinyCarts$]).pipe(
      map(([code, tinyCarts]) => tinyCarts.find((tinyCart) => tinyCart.code === code)),
      filter((tinyCart) => Boolean(tinyCart)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    const cartType$ = selectedTinyCart$.pipe(map((tinyCart) => tinyCart.cartType));
    const numberOfEntries$ = selectedTinyCart$.pipe(map((tinyCart) => tinyCart.numberOfEntries));
    const cart$: Observable<Cart | undefined> = cartType$.pipe(
      switchMap((cartType) =>
        this.activeCartService.getActiveCart(cartType, this.isViewCart).pipe(tap(() => (this.isViewCart = false)))
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    const entries$ = cart$.pipe(
      map((cart) => cart?.entries || []),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.hasDiscontinuedArticles$ = cart$.pipe(
      filter((cart) => !!cart),
      map((cart) => cart.removedItems?.length > 0),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.prePrintedLabelEntryMixedWithNonPrePrintedEntry$ = cart$.pipe(
      filter((cart) => cart?.cartType === CartType.stock),
      map((cart) => cart?.prePrintedLabelEntryMixedWithNonPrePrintedEntry),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const loading$ = combineLatest([
      cartType$.pipe(
        switchMap((cartType) => this.activeCartService.getLoading(cartType)),
        startWith(false)
      ),
      this.tinyCartService.getTinyCartsLoading(),
    ]).pipe(
      map(([activeCartLoading, tinyCartsLoading]) => activeCartLoading || tinyCartsLoading),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.cartType$ = cartType$.pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.cartErrors$ = cart$.pipe(switchMap((cart) => this.cartValidatorService.getCartErrors(cart)));
    this.tinyCarts$ = tinyCarts$;
    this.hasMultipleTinyCarts$ = this.tinyCartService.hasMultipleCarts().pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.notifications$ = cart$.pipe(
      map((cart) => cart?.notifications),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.totalPrice$ = cart$.pipe(map((cart) => cart?.subTotal || { value: 0 }));
    this.hasCartEditStatus$ = cart$.pipe(map((cart) => cart?.status === OrderStatus.APPROVER_EDITING));
    this.approvalOrderRef$ = cart$.pipe(map((cart) => cart?.approvalOrderRef));
    this.numberOfEntries$ = numberOfEntries$;
    this.loading$ = loading$;
    this.entries$ = entries$;
    this.showEmptyNotification$ = combineLatest([loading$, numberOfEntries$.pipe(startWith(<number | undefined>undefined))]).pipe(
      map(([loading, numberOfEntries]) => !loading && !(numberOfEntries > 0)),
      distinctUntilChanged()
    );

    this.isCheckoutEnabled$ = cartType$.pipe(
      switchMap((cartType) => this.checkoutService.isCheckoutEnabled(cartType)),
      startWith(false),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.isUserLoggedIn$ = this.authService.isUserLoggedIn();
    this.isCreditBlocked$ = this.soldToService.isCreditBlocked().pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.isMaxOrderLinesExceeded$ = this.cartType$.pipe(
      take(1),
      switchMap((cartType) => this.activeCartService.isMaxOrderLinesExceeded(cartType)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.accountingCodeList$ = this.cartAccountingCodesService
      .getAccountingCodeList()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.accountingCodeListLoading$ = this.cartAccountingCodesService.getAccountingCodeListLoading();

    this.accountingCodesMandatoryButNoneAvailable$ = this.cartAccountingCodesService
      .getAccountingCodesMandatoryButNoneAvailable()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.initAccountingCodesModalObservableOnCartDrawer();
  }

  private initAccountingCodesModalObservableOnCartDrawer(): void {
    this.subscriptions.add(
      this.cartAccountingCodesService
        .isAccountingCodeModalOpen()
        .pipe(
          filter((isOpen) => isOpen),
          switchMap(() =>
            this.cartAccountingCodesService.getActiveAccountingCode().pipe(
              withLatestFrom(this.cartAccountingCodesService.isAccountingCodesModalDismissed()),
              filter(([, isDismissed]) => !isDismissed)
            )
          )
        )
        .subscribe(() => {
          this.launchDialogService.openDialogAndSubscribe(LAUNCH_CALLER.ORDER_ENTRY_ACCOUNTING_CODES, undefined, this.cartType);
        })
    );
  }

  private openAbortCartEditModal(): void {
    this.launchDialogService.openDialogAndSubscribe(LAUNCH_CALLER.ORDER_APPROVAL_ABORT_HIJACK);

    this.subscriptions.add(
      this.launchDialogService.dialogClose
        .pipe(
          filter((reason) => reason !== undefined),
          take(1),
          filter((reason) => reason === ModalCloseReason.ORDER_APPROVAL_ABORT_HIJACK_CONFIRMED),
          switchMap(() => this.activeCartService.getActiveCart(this.cartType)),
          filter((cart) => !!cart),
          take(1)
        )
        .subscribe((cart) => this.orderApprovalService.abortOrderApprovalHijack('', cart.code))
    );
  }

  onGoToCheckout(): void {
    this.formGroupEntries.controls.filter((group) => {
      group.get('accountingCode').updateValueAndValidity({ emitEvent: false });
    });

    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      this.drawerCartEntriesTableChild.toArray().forEach((child) => child.markAsDirty());
      this.formGroup.updateValueAndValidity();

      const firstInvalidFormControl = this.elementRef.nativeElement.querySelector('#drawer-cart-entries-table form .ng-invalid');
      const antDrawerBodyElement = this.windowUtils.getElement('.tiny-cart-drawer-wrapper .ant-drawer-body') as HTMLElement;

      if (firstInvalidFormControl) {
        this.windowUtils.scrollIntoView(firstInvalidFormControl, antDrawerBodyElement);
      }

      return;
    }

    this.formGroup.markAsUntouched();
    this.cartAccountingCodesService.setAccountingCodesModalDismissed(false, this.cartType);

    this.scrollTopAfterClose();
    this.routingService.go({ cxRoute: 'checkout' }, { queryParams: this.cartTypeQueryParams });
  }

  onGoToCart(params): void {
    this.scrollTopAfterClose();
    this.routingService.go({ cxRoute: 'cart' }, { queryParams: params });
  }

  scrollTopAfterClose(): void {
    this.drawerService
      .getOpenDrawer()
      ?.afterClose.pipe(take(1))
      .subscribe(() => {
        this.viewportScroller.scrollToPosition([0, 0]);
      });
  }
}
